import { useState } from 'react';
import { Check, Copy } from 'lucide-react';
import { clientId } from '@/utils/clientId';

/* The client's ID, click to copy.
   Inline styles rather than utility classes because this sits in both admin
   design systems — the Tailwind client view and the `.adminx` mockup pages. */

type Props = {
  /** A client record, or the uid itself. */
  client: Record<string, any> | string | null | undefined;
  size?: 'sm' | 'md';
  /** Show the word "ID" before the code. */
  labelled?: boolean;
};

export default function ClientIdChip({ client, size = 'md', labelled = true }: Props) {
  const [copied, setCopied] = useState(false);
  const id = clientId(client);
  if (!id) return null;

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(id);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1400);
    } catch {
      /* clipboard blocked — the code is on screen to read anyway */
    }
  };

  const pad = size === 'sm' ? '2px 7px' : '4px 9px';
  const font = size === 'sm' ? 10.5 : 12;

  return (
    <button
      type="button"
      onClick={copy}
      title={copied ? 'Copied' : `Client ID ${id} — click to copy`}
      aria-label={`Client ID ${id}, click to copy`}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 5, padding: pad,
        borderRadius: 7, border: '1px solid #E2E8F0', background: copied ? '#E9F7EF' : '#F5F7FA',
        color: copied ? '#15803D' : '#475569', cursor: 'pointer', lineHeight: 1.2,
        fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Consolas, monospace',
        fontSize: font, fontWeight: 600, letterSpacing: '.02em', whiteSpace: 'nowrap',
      }}
    >
      {labelled && (
        <span style={{ fontFamily: 'inherit', fontSize: font - 1.5, opacity: .6, fontWeight: 700 }}>ID</span>
      )}
      {id}
      {copied
        ? <Check style={{ width: font, height: font, flex: 'none' }} />
        : <Copy style={{ width: font, height: font, flex: 'none', opacity: .5 }} />}
    </button>
  );
}
